import { useEffect, useRef, useState } from 'react';

/**
 * Dev-sync I — the Coil Analytics Start/End date pickers, ported from
 * main_header.tmpl:43-58 + main_header.js on merged dev
 * (onCoilAnalyticsDateFieldClick :442-447, _toggleCoilAnalyticsCalendar
 * :448-478, _renderCoilAnalyticsCalendar :491-531, onCoilAnalyticsCalPrev/
 * Next :533-550, onCoilAnalyticsCalDayClick :552-577,
 * _coilAnalyticsTodayIso :482-489).
 *
 * Markup ids/classes are the legacy ones byte-for-byte — the styling is the
 * committed static/css/report/skeleton.css:317-423 block (already loaded by
 * the board's REPORT_CSS cascade).
 *
 * Legacy semantics reproduced:
 *  - readonly inputs; clicking one toggles ITS calendar; only one panel open
 *    at a time (a conditional render — one `openField` — is the React
 *    equivalent of the legacy "hide every panel, then show one").
 *  - the view month opens on the currently-selected date's month.
 *  - prev-month nav is unlimited; next-month nav is DISABLED on the End field
 *    once the view reaches the current month of the current year (:500-503).
 *  - End-field days after today are disabled (:523); Start has no bound.
 *  - outside click (capture) closes the open panel (:467-477 — the legacy
 *    attaches the document listener in a setTimeout(0) so the opening click
 *    itself never closes it; the React effect attaches post-commit, same
 *    net effect).
 *  - picking a day writes the ISO value and closes the panel (:552-577).
 *
 * Value ownership is the PAGE's (the legacy keeps coilAnalyticsStartDate/
 * EndDate on the header view so edits survive re-renders, :123-134 — the
 * page-level state has the same lifetime).
 *
 * Visibility: applyConditionalUI on merged dev shows the row only while the
 * coil_analytics report is selected (`$(".coil-analytics-date-row").css(
 * 'display', isCoilAnalyticsReport ? 'flex' : 'none')`, :1281-1283).
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** the legacy isoDate helper (main_header.js:127-131 on merged dev) — local
 *  time, 'YYYY-MM-DD'. */
export function coilAnalyticsIsoDate(d: Date): string {
  const m = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  return `${d.getFullYear()}-${m}-${day}`;
}

type Field = 'start' | 'end';

interface CoilAnalyticsDateRangeProps {
  start: string; // ISO YYYY-MM-DD
  end: string;
  visible: boolean;
  onChange: (field: Field, iso: string) => void;
}

const CoilAnalyticsDateRange = ({ start, end, visible, onChange }: CoilAnalyticsDateRangeProps) => {
  const [openField, setOpenField] = useState<Field | null>(null);
  // the calendar's view month (1-12), seeded from the selected date at open.
  const [view, setView] = useState<{ year: number; month: number }>({ year: 0, month: 1 });
  const startFieldRef = useRef<HTMLSpanElement | null>(null);
  const endFieldRef = useRef<HTMLSpanElement | null>(null);

  // outside click (capture) closes — _toggleCoilAnalyticsCalendar :467-477.
  useEffect(() => {
    if (!openField) return undefined;
    const fieldEl = (openField === 'start' ? startFieldRef : endFieldRef).current;
    const onDocClick = (ev: MouseEvent) => {
      if (fieldEl && !fieldEl.contains(ev.target as Node)) setOpenField(null);
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [openField]);

  const toggle = (field: Field) => {
    if (openField === field) {
      setOpenField(null); // wasOpen → close (:458)
      return;
    }
    const iso = field === 'start' ? start : end;
    const parts = iso.split('-');
    setView({ year: +parts[0], month: +parts[1] }); // :460-463
    setOpenField(field);
  };

  const prevMonth = () =>
    setView((v) => (v.month <= 1 ? { year: v.year - 1, month: 12 } : { ...v, month: v.month - 1 }));
  const nextMonth = () =>
    setView((v) => (v.month >= 12 ? { year: v.year + 1, month: 1 } : { ...v, month: v.month + 1 }));

  const renderCalendar = (field: Field) => {
    const selectedIso = field === 'start' ? start : end;
    const todayIso = coilAnalyticsIsoDate(new Date());
    const { year, month } = view; // month 1-12
    const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, month, 0).getDate();
    // The end date can't be set past today (:499-503).
    const isFutureDisabled = field === 'end';
    const now = new Date();
    const isNextMonthDisabled =
      isFutureDisabled &&
      (year > now.getFullYear() ||
        (year === now.getFullYear() && month >= now.getMonth() + 1));

    const days: React.ReactNode[] = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      days.push(<span key={`e${i}`} className="coil-analytics-cal-day empty" />);
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      const iso = `${year}-${`0${month}`.slice(-2)}-${`0${d}`.slice(-2)}`;
      const isDisabled = isFutureDisabled && iso > todayIso;
      days.push(
        <span
          key={iso}
          className={`coil-analytics-cal-day${iso === selectedIso ? ' selected' : ''}${
            isDisabled ? ' disabled' : ''
          }`}
          data-field={field}
          data-date={iso}
          onClick={(e) => {
            // onCoilAnalyticsCalDayClick (:552-577) — empty/disabled cells are
            // excluded by the legacy delegate selector.
            e.preventDefault();
            e.stopPropagation();
            if (isDisabled) return;
            onChange(field, iso);
            setOpenField(null);
          }}
        >
          {d}
        </span>
      );
    }

    return (
      <div className="coil-analytics-calendar-panel" id={`coil_analytics_${field}_calendar`}>
        <div className="coil-analytics-cal-header">
          <button
            type="button"
            className="coil-analytics-cal-nav coil-analytics-cal-prev"
            data-field={field}
            onClick={(e) => {
              e.stopPropagation();
              prevMonth();
            }}
          >
            &#8249;
          </button>
          <span className="coil-analytics-cal-title">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button
            type="button"
            className="coil-analytics-cal-nav coil-analytics-cal-next"
            disabled={isNextMonthDisabled}
            data-field={field}
            onClick={(e) => {
              e.stopPropagation();
              if (!isNextMonthDisabled) nextMonth();
            }}
          >
            &#8250;
          </button>
        </div>
        <div className="coil-analytics-cal-weekdays">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="coil-analytics-cal-days">{days}</div>
      </div>
    );
  };

  const fieldBlock = (field: Field, label: string, value: string, ref: React.MutableRefObject<HTMLSpanElement | null>) => (
    <label className="coil-analytics-date-wrapper">
      <span>{label}</span>
      <span className="coil-analytics-date-field" id={`coil_analytics_${field}_field`} ref={ref}>
        <input
          type="text"
          readOnly
          id={`coil_analytics_${field}_date_display`}
          className="coil-analytics-date-input"
          data-field={field}
          value={value}
          onClick={(e) => {
            e.stopPropagation(); // onCoilAnalyticsDateFieldClick :442-447
            toggle(field);
          }}
          onChange={() => undefined}
        />
        {openField === field && renderCalendar(field)}
      </span>
    </label>
  );

  return (
    <div
      className="coil-analytics-date-row"
      style={{ display: visible ? 'flex' : 'none' }}
    >
      {fieldBlock('start', 'Start Date', start, startFieldRef)}
      {fieldBlock('end', 'End Date', end, endFieldRef)}
    </div>
  );
};

export default CoilAnalyticsDateRange;
